import { create } from 'zustand';

/**
 * @typedef {Object} Notification
 * @property {string} id - Unique notification ID
 * @property {string} type - Notification type: 'success', 'error', 'warning', 'info'
 * @property {string} title - Notification title
 * @property {string} message - Notification message
 * @property {number} duration - Auto-dismiss duration in milliseconds
 */

/**
 * UI store using Zustand
 * Manages application-wide UI state
 * 
 */
export const useUIStore = create((set, get) => ({
  // ======================================
  // Toast Management
  // ======================================
  
  // State
  toasts: [],

  // Actions
  /**
   * Add a new toast
   * @param {Object} toast - Toast object
   * @param {string} toast.type - Toast type
   * @param {string} toast.title - Toast title
   * @param {string} toast.message - Toast message
   * @param {number} [toast.duration] - Auto-dismiss duration
   */
  addToast: (toast) => {
    const id = Date.now().toString();
    const newToast = {
      id,
      type: 'info',
      duration: 5000,
      ...toast
    };

    set(state => ({
      toasts: [...state.toasts, newToast]
    }));

    // Auto-dismiss toast
    if (newToast.duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, newToast.duration);
    }

    return id;
  },

  /**
   * Remove toast by ID
   * @param {string} id - Toast ID to remove
   */
  removeToast: (id) => {
    set(state => ({
      toasts: state.toasts.filter(n => n.id !== id)
    }));
  },

  /**
   * Show success toast
   * @param {string} title - Toast title
   * @param {string} message - Toast message
   */
  showSuccessToast: (title, message) => {
    return get().addToast({
      type: 'success',
      title,
      message
    });
  },

  /**
   * Show error toast
   * @param {string} title - Toast title
   * @param {string} message - Toast message
   */
  showErrorToast: (title, message) => {
    return get().addToast({
      type: 'error',
      title,
      message,
      duration: 8000 // Longer duration for errors
    });
  },

  /**
   * Show warning toast
   * @param {string} title - Toast title
   * @param {string} message - Toast message
   */
  showWarningToast: (title, message) => {
    return get().addToast({
      type: 'warning',
      title,
      message
    });
  },

  /**
   * Show info toast
   * @param {string} title - Toast title
   * @param {string} message - Toast message
   */
  showInfoToast: (title, message) => {
    return get().addToast({
      type: 'info',
      title,
      message
    });
  },

  /**
   * Clear all toasts
   */
  clearToasts: () => {
    set({ toasts: [] });
  },

  // ======================================
  // Theme Management
  // ======================================

  theme: 'light', // 'light' | 'dark'

  /**
   * Toggle theme between light and dark
   */
  toggleTheme: () => {
    const currentTheme = get().theme;
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    set({ theme: newTheme });
    
    // Apply theme to document
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  },

  /**
   * Set theme
   * @param {'light' | 'dark'} theme - Theme to set
   */
  setTheme: (theme) => {
    set({ theme });
    document.documentElement.classList.toggle('dark', theme === 'dark');
  },

  // ======================================
  // Sidebar Management
  // ======================================
  sidebarOpen: false,

  /**
   * Toggle sidebar open/closed state
   */
  toggleSidebar: () => {
    set(state => ({ sidebarOpen: !state.sidebarOpen }));
  },

  /**
   * Set sidebar state
   * @param {boolean} open - Whether sidebar should be open
   */
  setSidebarOpen: (open) => {
    set({ sidebarOpen: open });
  },

  // ======================================
  // Modal Management
  // ======================================
  modals: [],

  /**
   * Open a modal
   * @param {string} type - Modal type
   * @param {Object} data - Modal data
   */
  openModal: (type, data = {}) => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Date.now().toString();
    const modal = {
      id,
      type,
      data,
      isOpen: true
    };

    set(state => ({
      modals: [...state.modals, modal]
    }));

    return id;
  },

  /**
   * Close modal by ID
   * @param {string} id - Modal ID to close
   */
  closeModal: (id) => {
    set(state => ({
      modals: state.modals.map(modal =>
        modal.id === id ? { ...modal, isOpen: false } : modal
      )
    }));
  },

  /**
   * Close all modals
   */
  closeAllModals: () => {
    set({ modals: [] });
  },
})); 